import { Tabs } from "antd";
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { InfoMode } from "../../components/Operation/InfoMode";
import { addRouterListener, getParamsFromUrl, getSearchFromUrl } from "../../util/library/Router";
import { selectMessage, selectPermissions } from "../../util/store/selectors";
import { AppState, getStore } from "../../util/store/store";
import { OP_STATUS, OPERATION_TYPE } from "../../util/varibles/constants";
import { ACTION } from "../../util/varibles/permission";
import { SOCKET_FUNC, SOCKET_NAME } from "../../web-socket/constants";
import WSService from "../../web-socket/web-socket-service";
import { messageActions } from "../MessagePage/reducer";
import Chat from "./Chat";
import { TAB_OPERATION_DETAIL } from "./constants";
import Detail from "./Detail";
import ProcessData from "./ProcessData";
import { ProcessDataProvider } from "./ProcessData/ProcessDataContext";
import { opDetailActions } from "./reducer";
import styles from './style.module.scss';
import Title from "./Title";
import TitleRight from "./Title/TitleRight";

const Body = () => {
    const dispatch = useDispatch();
    const [permissionMessager] = useSelector(selectPermissions([ACTION.MESSENGER.OPERATION]));
    const keyTab = useSelector((state: AppState) => state.operationDetail.keyTab);
    const [activeTab, setActiveTab] = useState<string>(keyTab);
    const infoMode = useSelector((state: AppState) => state.operationDetail.infoMode);
    const firstRealStartTime = useSelector((state: AppState) => state.operationDetail.data?.activity_log?.[0]?.real_start_time);
    const operation_type = useSelector((state: AppState) => state.operationDetail.data?.operation?.operation_type);
    const status = useSelector((state: AppState) => state.operationDetail.data?.operation?.status);
    const showInfoMode = activeTab === TAB_OPERATION_DETAIL.INFO
        && (operation_type === OPERATION_TYPE.HARVEST
            || operation_type === OPERATION_TYPE.TREATMENT);
    const items = [{
        label: 'Operation info',
        key: 'info',
        children: <Detail />,
    }];

    if (new Set([OPERATION_TYPE.HARVEST, OPERATION_TYPE.TREATMENT]).has(operation_type) && new Set([OP_STATUS.PROCESSING, OP_STATUS.FINISHED]).has(status)) {
        items.push({
            label: 'Process data',
            key: 'process_data',
            children: <ProcessDataProvider>
                <ProcessData />
            </ProcessDataProvider>,
        })
    }

    if (permissionMessager) {
        items.push({
            label: 'Message',
            key: 'message',
            children: <Chat />,
        })
    }

    return <Tabs
        defaultActiveKey={keyTab}
        onChange={setActiveTab}
        centered
        classNames={{ header: styles.headerPage }}
        tabBarExtraContent={{
            left: <Title />,
            right: <div className={styles.headerActions}>
                {showInfoMode && <div className={styles.infoMode}>
                    <InfoMode
                        value={infoMode}
                        actualDisabled={!firstRealStartTime}
                        onChange={(value) => dispatch(opDetailActions.setInfoMode(value))}
                    />
                </div>}
                <TitleRight />
            </div>,
        }}
        tabBarGutter={10}
        items={items}
    />
}

const OperationDetailPage: React.FC = () => {
    const dispatch = useDispatch();

    const rootRef = useRef<HTMLDivElement>(null);
    const operationRef = useRef({ tenant_id: '', id: '' });
    const isOpenSocketRef = useRef(false);
    const socketRef = useRef<WSService | undefined>(undefined);
    const groupIdsRef = useRef<any[]>([]);

    const handleGetOperation = () => {
        const { tenant_id = '', id = '' } = getParamsFromUrl();
        operationRef.current = { tenant_id, id };
        const [tab] = getSearchFromUrl();
        isOpenSocketRef.current = false;

        dispatch(opDetailActions.getOpRequest({
            ...operationRef.current,
            tab: tab === TAB_OPERATION_DETAIL.MESSAGE
                ? TAB_OPERATION_DETAIL.MESSAGE
                : TAB_OPERATION_DETAIL.INFO,
        }));

        const messages = selectMessage.messages(getStore());
        if (!messages[id]) {
            dispatch(messageActions.getMessagesRequest({ id, index: 1 }));
        }
    };

    const registerWebSocket = () => {
        if (!isOpenSocketRef.current) {
            const { id, tenant_id } = operationRef.current;
            groupIdsRef.current = [id];
            socketRef.current = new WSService({
                groups: groupIdsRef.current,
                listener: {
                    lev: SOCKET_NAME.PAGE,
                    func: (value: any) => {
                        switch (value.function) {
                            case SOCKET_FUNC.VESSEL_GPS:
                                dispatch(opDetailActions.updatePositionVessel(value));
                                break;
                            case SOCKET_FUNC.OPERATION_CONFIRMED:
                            case SOCKET_FUNC.OPERATION_STATE_CHANGE:
                            case SOCKET_FUNC.OPERATION_STATUS_CHANGE:
                                dispatch(opDetailActions.updateOp(value.message));
                                break;
                            case SOCKET_FUNC.OPERATION_ROUTE_CHANGE:
                                dispatch(opDetailActions.getActualRoute({ id, tenant_id }));
                                break;
                            default:
                                return;
                        }
                    },
                },
            });
            isOpenSocketRef.current = true;
        }
    };

    const handleClose = () => {
        WSService.close(SOCKET_NAME.PAGE, groupIdsRef.current);
    };

    useLayoutEffect(() => {
        handleGetOperation();

        const listen = addRouterListener((state) => {
            const { pathname } = state.location;
            if (pathname.indexOf('/operation') === 0) {
                const { tenant_id = '', id = '' } = getParamsFromUrl(state);
                if (operationRef.current.tenant_id === tenant_id && operationRef.current.id === id) return;
                if (socketRef.current) {
                    WSService.close(SOCKET_NAME.PAGE, groupIdsRef.current);
                }
                handleGetOperation();
                registerWebSocket();
            }
        });

        const timer = setTimeout(() => {
            registerWebSocket();
        }, 2000);

        return () => {
            handleClose();
            clearTimeout(timer);
            if (listen) listen();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div ref={rootRef} className={[styles.page, 'bgPage'].join(' ')}>
            <Body />
        </div>
    );
};

export default OperationDetailPage;
